import type {
  NavHiddenTabs,
  NavTabsConfig,
} from '~/(tenant)/_lib/schema/landing-config.schema';

import { pickLocalized } from '../landing/landing-context';

/**
 * The built-in signed-in tabs and the pure logic that decides which of them
 * render, in what order, under what label.
 *
 * Extracted from site-navigation.tsx (a 'use client' component) so the ordering
 * and label-override rules — the part with real edge cases: partial orders,
 * duplicate ids, blank overrides, Arabic fallback — can be unit-tested without
 * pulling React in. The component imports from here; nothing else changes.
 */
export const authRequiredLinks = {
  Bookings: {
    i18nKey: 'common:memberClub.nav.bookings',
    label: 'Bookings',
    path: '/bookings',
  },
  Packages: {
    i18nKey: 'common:memberClub.nav.myPackages',
    label: 'My Packages',
    path: '/packages',
  },
  Memberships: {
    i18nKey: 'common:memberClub.nav.myMemberships',
    label: 'My Memberships',
    path: '/memberships',
  },
};

export type AuthTabKey = keyof typeof authRequiredLinks;
type AuthTabId = NonNullable<NavTabsConfig['authTabOrder']>[number];

// Maps each authRequiredLinks key to its Website-Builder hide flag. Typed as a
// full Record over the keys, so renaming/adding a tab fails at compile time
// instead of silently skipping the filter. Keeps the desktop and mobile menus
// in lockstep; fails OPEN (shown) for anything unmapped or when no flags are set.
const HIDE_FLAG_BY_TAB: Record<AuthTabKey, keyof NavHiddenTabs> = {
  Bookings: 'hideBookings',
  Packages: 'hidePackages',
  Memberships: 'hideMemberships',
};

// authTabOrder speaks in lowercase ids; this map bridges them back to the
// PascalCase authRequiredLinks keys. A full Record so adding a tab is a compile
// error until it is updated.
const KEY_BY_TAB_ID: Record<AuthTabId, AuthTabKey> = {
  bookings: 'Bookings',
  packages: 'Packages',
  memberships: 'Memberships',
};

// Narrow to the string-valued label keys (NOT `keyof NavTabsConfig`, which also
// includes the boolean flags and the authTabOrder array) so indexing `tabs`
// yields `string | undefined` directly — no `as` casts at the call site.
type TabLabelKey = 'labelBookings' | 'labelPackages' | 'labelMemberships';
type TabLabelArKey =
  | 'labelBookings_ar'
  | 'labelPackages_ar'
  | 'labelMemberships_ar';

const LABEL_FIELD_BY_TAB: Record<AuthTabKey, TabLabelKey> = {
  Bookings: 'labelBookings',
  Packages: 'labelPackages',
  Memberships: 'labelMemberships',
};

const LABEL_AR_FIELD_BY_TAB: Record<AuthTabKey, TabLabelArKey> = {
  Bookings: 'labelBookings_ar',
  Packages: 'labelPackages_ar',
  Memberships: 'labelMemberships_ar',
};

export function isAuthTabHidden(key: string, tabs?: NavTabsConfig): boolean {
  if (!tabs) return false;

  const flag = HIDE_FLAG_BY_TAB[key as AuthTabKey];

  return flag ? tabs[flag] === true : false;
}

/**
 * Built-in tabs in the tenant's configured order (authTabOrder), with any
 * omitted id appended in default order so a partial array never drops a tab,
 * then hidden tabs filtered out. Shared by desktop + mobile so they stay in
 * lockstep.
 */
export function orderedVisibleAuthTabs(tabs?: NavTabsConfig): AuthTabKey[] {
  const allKeys = Object.keys(authRequiredLinks) as AuthTabKey[];
  const order = tabs?.authTabOrder;
  let ordered: AuthTabKey[];

  if (order && order.length > 0) {
    const seen = new Set<AuthTabKey>();

    ordered = [];

    for (const id of order) {
      const key = KEY_BY_TAB_ID[id];

      if (key && !seen.has(key)) {
        ordered.push(key);
        seen.add(key);
      }
    }

    for (const key of allKeys) if (!seen.has(key)) ordered.push(key);
  } else {
    ordered = allKeys;
  }

  return ordered.filter((key) => !isAuthTabHidden(key, tabs));
}

/**
 * Tenant label override for a built-in tab (localized), or undefined to fall
 * back to the default i18n label. Blank/whitespace-only overrides are treated
 * as "no override", which is what lets the builder persist a raw in-progress
 * value without it ever reaching the live nav.
 */
export function authTabLabelOverride(
  key: AuthTabKey,
  tabs: NavTabsConfig | undefined,
  lang: string,
): string | undefined {
  if (!tabs) return undefined;

  const label = pickLocalized(
    tabs[LABEL_FIELD_BY_TAB[key]],
    tabs[LABEL_AR_FIELD_BY_TAB[key]],
    lang,
  );

  return label && label.trim() !== '' ? label : undefined;
}
